import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {
  ProcessFlowchartEdgeType,
  TransitionSource,
  TaskSchemaNodeSection,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  getDefaultTransitionSource,
  getNodeSectionTypeFromDataFlowEdge,
  getTransitionSourceByNodeIdPairKey,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';

import {findAllUpstreamMappableNodeIds} from '../TraverseFlowchart';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';

function TransitionSourceSelector(props: {
  transitionSource: TransitionSource;
  hasOutputSection: boolean;
  onSelect: (value: TransitionSource) => void;
  displayName: string;
  locked?: boolean;
}): JSX.Element {
  const options: Array<{
    label: string | JSX.Element;
    secondaryLabel?: JSX.Element;
    value: TransitionSource;
  }> = [
    {
      label: (
        <span className={css(styles.primaryText)}>
          For every task in{' '}
          <span className={css(styles.flowchartSettingDescriptionBold)}>{props.displayName}</span>
        </span>
      ),
      secondaryLabel: (
        <span className={css(styles.secondaryText)}>
          This limits the available field mappings from{' '}
          <span className={css(styles.flowchartSettingDescriptionBold)}>{props.displayName}</span> to input
          fields only.
        </span>
      ),
      value: TransitionSource.INPUTS,
    },
  ];

  if (props.hasOutputSection) {
    options.unshift({
      label: (
        <span className={css(styles.primaryText)}>
          For every output in{' '}
          <span className={css(styles.flowchartSettingDescriptionBold)}>{props.displayName}</span>
        </span>
      ),
      value: TransitionSource.OUTPUTS,
    });
  }

  return (
    <div
      className={css(styles.buttonGroup)}
      {...getTestProps({component: 'RadioButtonGroup', element: 'root'})}
    >
      <RadioButtonGroup
        title=""
        selectedValue={props.transitionSource}
        onChange={props.onSelect}
        hasBottomSpacing={false}
        options={options}
        disabled={props.locked}
      />
    </div>
  );
}

export interface FlowchartSettingsTabContentProps {
  locked?: boolean;
}

function EmptySettingsContent(props: {selectedNodeName: string}): JSX.Element {
  const {selectedNodeName} = props;
  return (
    <div className={css(styles.emptyStateContainer)}>
      <div className={css(styles.flowchartSettingDescription)}>
        There are no mappings to configure for{' '}
        <span className={css(styles.flowchartSettingDescriptionBold)}>{selectedNodeName}</span>.
      </div>
    </div>
  );
}

interface SettingsContentProps {
  selectedNodeName: string;
  connectedNodes: Array<TaskSchemaNode>;
  transitionSources: Record<string, TransitionSource>;
  handleRadioSelect: (edgeIdToUpdate: string, value: TransitionSource) => void;
  locked?: boolean;
}

function SettingsContent(props: SettingsContentProps): JSX.Element {
  const {selectedNodeName, connectedNodes, transitionSources, handleRadioSelect, locked} = props;

  return (
    <>
      <div className={css(styles.flowchartSettingDescription)}>
        When this workflow is run, create 1 task in{' '}
        <span className={css(styles.flowchartSettingDescriptionBold)}>{selectedNodeName}</span>
      </div>
      {connectedNodes.map((node) => {
        const nodeDisplayName = node.data.nodeName;

        return (
          <TransitionSourceSelector
            key={node.id}
            transitionSource={transitionSources[node.id]}
            hasOutputSection={node.data.sections.some(
              (section) => section.sectionName === TaskSchemaNodeSection.OUTPUTS
            )}
            onSelect={(value: TransitionSource) => {
              handleRadioSelect(node.id, value);
            }}
            displayName={nodeDisplayName}
            locked={locked}
          />
        );
      })}
    </>
  );
}

export default function FlowchartSettingsPane(props: FlowchartSettingsTabContentProps): JSX.Element {
  const {
    selectedNode,
    interactiveFlowchart,
    transitionSourceByNodeIdPair,
    onChangeTransitionSource,
    onChangeFlowchart,
    onChangeCustomMappingData,
  } = NodeDetailsSidepanelContext.useContext();

  const {upstreamMappableNodeIds, transitionSources} = useMemo(() => {
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
    const transitionSources: Record<string, TransitionSource> = {};
    for (const sourceNodeId of upstreamMappableNodeIds) {
      const nodeIdPair = getTransitionSourceByNodeIdPairKey(sourceNodeId, selectedNode.id);
      transitionSources[sourceNodeId] =
        transitionSourceByNodeIdPair[nodeIdPair] ??
        getDefaultTransitionSource(
          sourceNodeId,
          interactiveFlowchart.nodes.map((node) => node.data)
        );
    }
    return {upstreamMappableNodeIds, transitionSources};
  }, [interactiveFlowchart, selectedNode.id, transitionSourceByNodeIdPair]);

  const {nodes, edges} = interactiveFlowchart;
  const connectedNodes = Object.values(nodes).filter((node) => upstreamMappableNodeIds.includes(node.id));

  const handleRadioSelect = (upstreamMappableNodeIdToUpdate: string, value: TransitionSource): void => {
    const currentTransitionSource = transitionSources[upstreamMappableNodeIdToUpdate];
    const nodeIdPair = getTransitionSourceByNodeIdPairKey(upstreamMappableNodeIdToUpdate, selectedNode.id);
    if (currentTransitionSource === TransitionSource.OUTPUTS && value === TransitionSource.INPUTS) {
      // If we are changing the transition source from outputs to inputs, we need to clear all mappings from
      // the output fields of the source node.
      const dataFlowEdgesToClear = edges.filter((edge) => {
        if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
          return false;
        }
        if (edge.target !== selectedNode.id || edge.source !== upstreamMappableNodeIdToUpdate) {
          return false;
        }
        const sourceFieldSectionType = getNodeSectionTypeFromDataFlowEdge(
          edge,
          nodes.map((node) => node.data)
        );
        return sourceFieldSectionType === TaskSchemaNodeSectionType.OUTPUTS;
      });

      for (const edge of dataFlowEdgesToClear) {
        onChangeCustomMappingData(edge.id, {});
      }

      onChangeFlowchart({
        nodeChanges: [],
        edgeChanges: dataFlowEdgesToClear.map((edge) => ({id: edge.id, type: 'remove'})),
      });
    }
    onChangeTransitionSource(nodeIdPair, value);
  };

  return (
    <div className={css(styles.tabContentContainer)} key={selectedNode.id}>
      <div className={css(styles.settingsGroup)}>
        <div className={css(styles.flowchartSettingTitle)}>Task Creation Settings</div>
        {connectedNodes.length === 0 ? (
          <EmptySettingsContent selectedNodeName={selectedNode.data.nodeName} />
        ) : (
          <SettingsContent
            selectedNodeName={selectedNode.data.nodeName}
            connectedNodes={connectedNodes}
            transitionSources={transitionSources}
            handleRadioSelect={handleRadioSelect}
            locked={props.locked}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  tabContentContainer: {
    padding: '16px',
    marginTop: '-23px',
  },
  settingsGroup: {},
  flowchartSettingTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: Color.GRAY_900,
    marginBottom: '4px',
  },
  flowchartSettingDescription: {
    fontSize: '14px',
    fontWeight: 400,
    color: Color.GRAY_900,
    display: 'flex',
    marginBottom: '10px',
  },
  flowchartSettingDescriptionBold: {
    fontWeight: 600,
    paddingLeft: '4px',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${Color.GRAY_300}`,
    borderRadius: '6px',
    padding: '10px',
    marginBottom: '10px',
  },
  primaryText: {
    color: Color.GRAY_900,
  },
  secondaryText: {
    color: Color.GRAY_600,
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
});
